import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trendingApi } from '../../lib/api';

// Maps a URL pathname to the Trending section it belongs to, or null if the page
// isn't a trackable item detail page (listing pages, guide pages, and everything
// outside these 4 sections are excluded). Any future article/topic/tool/simulator
// added under the existing route conventions is picked up automatically — no wiring
// needed beyond adding its route, since this matches on path shape, not a hardcoded
// list of known items.
export const resolveTrendingVisit = (pathname) => {
  // A full page load (not an in-app <Link> navigation) of e.g. /articles/bms can get
  // 301-redirected to /articles/bms/ by static-file serving, since
  // frontend/public/articles/bms/ is a real directory (article figure images). Strip
  // one trailing slash so that doesn't split visit identity from the canonical,
  // slash-free ids used by the candidate lists in ArticlesPage/HowItWorksPage/etc.
  const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (p.startsWith('/articles/') && !p.startsWith('/articles/section/')) {
    return { section: 'article', item_id: p };
  }
  if (p.startsWith('/how-it-works/') && !p.startsWith('/how-it-works/section/')) {
    return { section: 'how_it_works', item_id: p };
  }
  if (p.startsWith('/simulation/') && !p.endsWith('/guide')) {
    return { section: 'simulation', item_id: p };
  }
  if (p.startsWith('/tools/') && p !== '/tools' && !p.endsWith('/guide')) {
    return { section: 'tool', item_id: p };
  }
  return null;
};

// Mounted once near the app root. Records one visit per navigation to an item detail
// page, powering the Trending sections on Articles/How Does It Work?/Simulation/Tools.
// Fire-and-forget: never blocks rendering and swallows errors, since a failed visit
// record should never be visible to the user.
const PageVisitTracker = () => {
  const location = useLocation();
  const lastRecordedRef = useRef(null);

  useEffect(() => {
    if (lastRecordedRef.current === location.pathname) return;
    lastRecordedRef.current = location.pathname;

    const visit = resolveTrendingVisit(location.pathname);
    if (!visit) return;

    trendingApi.recordVisit(visit.section, visit.item_id).catch(() => {});
  }, [location.pathname]);

  return null;
};

export default PageVisitTracker;
